import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';
import ScrollToTop, { RouteScroll } from './ScrollToTop';
import { btnPrimary, btnSm } from './ui';
import { CLIENT_PORTAL_URL, CONTACT_ADDRESS, CONTACT_EMAIL } from '../data/products';

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `text-[0.82rem] transition-colors ${isActive ? 'text-paper' : 'text-muted hover:text-paper'}`;

const SiteShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const year = new Date().getFullYear();

  const close = () => setOpen(false);

  return (
    <div className="min-h-screen bg-ink text-paper">
      <RouteScroll />
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[80] focus:rounded-full focus:bg-amber focus:px-4 focus:py-2 focus:text-[#1a1200]"
      >
        Skip to content
      </a>
      <header className="sticky top-0 z-50 border-b border-line bg-ink/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-[1220px] items-center justify-between gap-3 px-4 sm:px-6">
          <Link to="/" aria-label={t('nav.homeAria')} onClick={close} className="shrink-0">
            <Logo />
          </Link>
          <nav className="hidden items-center gap-6 lg:flex" aria-label={t('nav.primaryAria')}>
            <NavLink to="/" end className={navLinkClass}>
              {t('nav.home')}
            </NavLink>
            <Link to="/#how" className="text-[0.82rem] text-muted transition-colors hover:text-paper">
              {t('nav.how')}
            </Link>
            <NavLink to="/products" className={navLinkClass}>
              {t('nav.products')}
            </NavLink>
            <Link to="/#faq" className="text-[0.82rem] text-muted transition-colors hover:text-paper">
              {t('nav.faq')}
            </Link>
            <Link to="/#talk" className="text-[0.82rem] text-muted transition-colors hover:text-paper">
              {t('nav.contact')}
            </Link>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSwitcher />
            <Link to="/#talk" className={`${btnPrimary} ${btnSm} hidden sm:inline-flex`}>
              {t('nav.cta')}
            </Link>
            <button
              type="button"
              className="inline-flex rounded-lg p-2 text-muted hover:text-paper lg:hidden"
              aria-expanded={open}
              aria-label={open ? t('nav.closeMenu') : t('nav.openMenu')}
              onClick={() => setOpen((value) => !value)}
            >
              {open ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
            </button>
          </div>
        </div>
        {open && (
          <nav className="border-t border-line px-4 py-3 lg:hidden" aria-label={t('nav.primaryAria')}>
            <div className="flex flex-col gap-1">
              <Link to="/" onClick={close} className="rounded-lg px-3 py-2 text-muted hover:bg-panel hover:text-paper">
                {t('nav.home')}
              </Link>
              <Link to="/#how" onClick={close} className="rounded-lg px-3 py-2 text-muted hover:bg-panel hover:text-paper">
                {t('nav.how')}
              </Link>
              <Link to="/products" onClick={close} className="rounded-lg px-3 py-2 text-muted hover:bg-panel hover:text-paper">
                {t('nav.products')}
              </Link>
              <Link to="/#faq" onClick={close} className="rounded-lg px-3 py-2 text-muted hover:bg-panel hover:text-paper">
                {t('nav.faq')}
              </Link>
              <Link to="/#talk" onClick={close} className="rounded-lg px-3 py-2 text-muted hover:bg-panel hover:text-paper">
                {t('nav.contact')}
              </Link>
              <Link to="/#talk" onClick={close} className={`${btnPrimary} mt-2`}>
                {t('nav.cta')}
              </Link>
            </div>
          </nav>
        )}
      </header>
      <main id="main">{children}</main>
      <footer className="border-t border-line">
        <div className="mx-auto grid max-w-[1180px] gap-8 px-4 py-12 sm:px-6 md:grid-cols-3">
          <div>
            <Logo />
            <p className="mt-4 max-w-sm text-sm text-muted">{t('footer.blurb')}</p>
            <p className="mt-3 font-mono text-xs text-stamp">{t('footer.tagline')}</p>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-paper">{t('footer.explore')}</h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li><Link className="text-muted hover:text-amber" to="/">{t('nav.home')}</Link></li>
              <li><Link className="text-muted hover:text-amber" to="/products">{t('nav.products')}</Link></li>
              <li><Link className="text-muted hover:text-amber" to="/products/simpian-routing">{t('prod.routing.name')}</Link></li>
              <li><Link className="text-muted hover:text-amber" to="/products/bilingual-retail-last-mile">{t('prod.retail.name')}</Link></li>
              <li><Link className="text-muted hover:text-amber" to="/products/factory-ops">{t('prod.factory.name')}</Link></li>
              <li><a className="text-muted hover:text-amber" href="/privacy/">Privacy Policy</a></li>
            </ul>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-paper">{t('footer.visit')}</h2>
            <address className="mt-3 space-y-2 text-sm not-italic text-muted">
              <p>{t('cta.addressName')}</p>
              <p>{CONTACT_ADDRESS}</p>
              <p>
                <a className="text-amber hover:text-mint" href={`mailto:${CONTACT_EMAIL}`}>
                  {CONTACT_EMAIL}
                </a>
              </p>
              <p className="font-mono text-xs text-stamp">{t('cta.geo')}</p>
              <p>
                <a className="text-muted hover:text-amber" href={CLIENT_PORTAL_URL} rel="noopener noreferrer">
                  {t('footer.portal')}
                </a>
              </p>
            </address>
          </div>
        </div>
        <div className="border-t border-line px-4 py-4 text-center text-xs text-muted">
          {t('footer.copyright', { year })}
        </div>
      </footer>
      <ScrollToTop />
    </div>
  );
};

export default SiteShell;
